import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

const VARIANT_FETCH_ERROR_EVENT = 'variant:fetch-error';
const STICKY_CART_ERROR_MESSAGE = 'Eklenemedi · Tekrar deneyin';
const STICKY_VARIANT_ERROR_MESSAGE = 'Yüklenemedi · Tekrar deneyin';
const SUCCESS_BUTTON_FEEDBACK_DURATION = 3000;

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLElement} errorFeedback - Visible sticky cart error description
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber', 'errorFeedback'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {ResizeObserver | null} */
  #headerResizeObserver = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {HTMLElement | null} */
  #footer = null;

  #headerOffset = 0;

  /** @type {number | null} */
  #visibilityFrame = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #isSubmitting = false;

  /** @type {boolean} */
  #variantWaiting = false;

  /** @type {number | undefined} */
  #errorResetTimeout;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#setStickyBarState(this.refs.stickyBar.dataset.stuck === 'true');
    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(VARIANT_FETCH_ERROR_EVENT, this.#handleVariantFetchError, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('resize', this.#setupIntersectionObserver, { signal });
    window.addEventListener('scroll', this.#queueVisibility, { passive: true, signal });

    const header = document.querySelector('#header-group');
    if (header) {
      this.#headerResizeObserver = new ResizeObserver(this.#setupIntersectionObserver);
      this.#headerResizeObserver.observe(header);
    }
    this.#dialogObserver = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'attributes' ||
        [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (node.matches('dialog[open]') || node.querySelector('dialog[open]'))))) {
        this.#syncVisibility();
      }
    });
    this.#dialogObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });

    this.#getInitialQuantity();
    this.#ensureAccessibleLabel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#headerResizeObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.#abortController.abort();
    if (this.#visibilityFrame !== null) {
      cancelAnimationFrame(this.#visibilityFrame);
      this.#visibilityFrame = null;
    }
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
    if (this.#errorResetTimeout) {
      clearTimeout(this.#errorResetTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver = () => {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#targetAddToCartButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
    if (!this.#targetAddToCartButton) {
      this.#setStickyBarState(false);
      return;
    }

    const header = document.querySelector('#header-group');
    const headerPosition = header ? getComputedStyle(header).position : '';
    this.#headerOffset = header && ['sticky', 'fixed'].includes(headerPosition)
      ? Math.max(0, Math.ceil(header.getBoundingClientRect().bottom)) : 0;
    this.#footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(this.#syncVisibility, {
      rootMargin: `-${this.#headerOffset}px 0px 0px 0px`,
      threshold: [0, 1],
    });
    this.#buyButtonsIntersectionObserver.observe(this.#targetAddToCartButton);
    if (this.#footer) {
      this.#mainBottomObserver = new IntersectionObserver(this.#syncVisibility, { rootMargin: '0px 0px 96px 0px' });
      this.#mainBottomObserver.observe(this.#footer);
    }
    this.#syncVisibility();
  };

  #queueVisibility = () => {
    if (this.#visibilityFrame !== null) return;
    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      if (this.isConnected) this.#syncVisibility();
    });
  };

  #syncVisibility = () => {
    const buttonRect = this.#targetAddToCartButton?.getBoundingClientRect();
    const footerRect = this.#footer?.getBoundingClientRect();
    const nearFooter = footerRect && footerRect.top < window.innerHeight + 96 && footerRect.bottom > 0;
    const openDialog = document.querySelector('dialog[open]');
    const shouldShow = Boolean(buttonRect && buttonRect.height > 0 && buttonRect.top < this.#headerOffset &&
      !nearFooter && !openDialog && this.dataset.variantAvailable === 'true');
    if (shouldShow !== this.#isStuck) this.#setStickyBarState(shouldShow);
  };

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton || this.#targetAddToCartButton.disabled || this.#isSubmitting || this.#variantWaiting) return;

    const productForm = /** @type {any} */ (this.#getProductForm());
    const form = this.#targetAddToCartButton.closest('form');
    if (!productForm || !form) return;
    if (!form.checkValidity()) {
      const invalidField = /** @type {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null} */ (
        form.querySelector('input:invalid, select:invalid, textarea:invalid')
      );
      if (invalidField) {
        this.#setStickyBarState(false);
        invalidField.focus({ preventScroll: true });
        const fieldTop = invalidField.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, fieldTop - this.#headerOffset - 32), behavior: 'instant' });
        invalidField.reportValidity();
      } else {
        form.reportValidity();
      }
      return;
    }

    const quantitySelector = productForm.refs?.quantitySelector;
    if (quantitySelector?.canAddToCart && !quantitySelector.canAddToCart().canAdd) {
      this.#targetAddToCartButton.click();
      return;
    }

    this.#isSubmitting = true;
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  #showSuccessFeedback = async () => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    let flyToCartReady = false;
    if (cartIcon && this.refs.productImage) {
      try {
        await import('@theme/fly-to-cart');
        flyToCartReady = Boolean(customElements.get('fly-to-cart'));
      } catch {
        flyToCartReady = false;
      }
    }

    if (flyToCartReady && cartIcon?.isConnected && this.refs.productImage?.isConnected) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = this.refs.productImage;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);
      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    } else {
      await onAnimationEnd(this.refs.addToCartButton);
    }

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, SUCCESS_BUTTON_FEEDBACK_DURATION);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.#setStickyBarState(currentStuck === 'true');
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
    this.#setupIntersectionObserver();

    this.#ensureAccessibleLabel();
    this.#variantWaiting = false;
    if (this.#isSubmitting) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.setAttribute('aria-busy', 'true');
    } else {
      this.refs.addToCartButton.removeAttribute('aria-busy');
      this.refs.addToCartButton.disabled = this.dataset.variantAvailable !== 'true';
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
    this.#variantWaiting = true;
    this.refs.addToCartButton.disabled = true;
    this.refs.addToCartButton.setAttribute('aria-busy', 'true');
  };

  /** @param {CustomEvent<{productId?: string}>} event */
  #handleVariantFetchError = (event) => {
    if (event.detail?.productId && event.detail.productId !== this.dataset.productId) return;

    this.#variantWaiting = false;
    if (!this.#isSubmitting) {
      this.refs.addToCartButton.removeAttribute('aria-busy');
      this.refs.addToCartButton.disabled = this.dataset.variantAvailable !== 'true';
    }
    this.#showErrorFeedback(STICKY_VARIANT_ERROR_MESSAGE);
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    if (!this.#isSubmitting || event.target !== this.#getProductForm()) return;

    const didError = event.type === ThemeEvents.cartError || event.detail?.data?.didError === true;

    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    this.#isSubmitting = false;
    if (!this.#variantWaiting) {
      this.refs.addToCartButton.removeAttribute('aria-busy');
    }
    this.refs.addToCartButton.disabled = this.#variantWaiting || this.dataset.variantAvailable !== 'true';

    if (!didError) {
      this.#showSuccessFeedback();
    } else {
      this.#showErrorFeedback(STICKY_CART_ERROR_MESSAGE);
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Keeps visual visibility and keyboard/accessibility-tree availability in sync.
   * @param {boolean} isStuck
   */
  #setStickyBarState(isStuck) {
    const { stickyBar } = this.refs;
    this.#isStuck = isStuck;
    stickyBar.dataset.stuck = String(isStuck);
    stickyBar.toggleAttribute('inert', !isStuck);
    stickyBar.setAttribute('aria-hidden', String(!isStuck));
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  #ensureAccessibleLabel() {
    const button = this.refs.addToCartButton;
    const action = button.querySelector('.add-to-cart-text__content')?.textContent?.replace(/\s+/g, ' ').trim();
    const productTitle = this.querySelector('.sticky-add-to-cart__title')?.textContent?.replace(/\s+/g, ' ').trim();
    const label = [action, productTitle].filter(Boolean).join(': ');

    if (label) {
      button.setAttribute('aria-label', label);
    }
  }

  /** @param {string} message */
  #showErrorFeedback(message) {
    if (this.#errorResetTimeout) clearTimeout(this.#errorResetTimeout);

    const { addToCartButton, errorFeedback } = this.refs;
    errorFeedback.textContent = message;
    addToCartButton.setAttribute('aria-describedby', errorFeedback.id);

    this.#errorResetTimeout = setTimeout(() => {
      if (errorFeedback.isConnected) {
        errorFeedback.textContent = '';
        addToCartButton.removeAttribute('aria-describedby');
      }
    }, 5000);
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
